import React from 'react';
import styled from 'styled-components';
import { reduce, uniqueId } from 'lodash';
import PropTypes from 'prop-types';
import { ArrowDown, ArrowUp } from '@origo_vl_banking/icons';

import LabelContainer from '../inputs/components/LabelContainer';
import StyledLabel from '../inputs/components/StyledLabel';

const MultiSelectContainer = styled.div`
  font-size: 14px;
  border-radius: ${props =>
    (props.theme.borderRadius
      ? props.expanded
        ? `${props.theme.borderRadius} ${props.theme.borderRadius} 0px 0px`
        : props.theme.borderRadius
      : 0)};
  border: ${props =>
    (props.error ? `3px solid ${props.theme.danger}` : `thin solid ${props.theme.borderColor}`)};
  width: ${props => props.width};
  cursor: default;
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
  font-family: Roboto;
  font-size: 16px;
  text-overflow: ellipsis;
  white-space: nowrap;
  /* &:focus {
    ${props => props.expanded && 'outline: none;'};
  } */
  outline: none;
  background: #fff;

  @media only screen and (max-width: 768px) {
    width: 100%;
  }
`;

const MultiSelectContentContainer = styled(MultiSelectContainer)`
  display: inherit;
  position: absolute;
  z-index: 100;
  padding-bottom: 4px;
  border-radius: ${props => (props.expanded ? '0px 0px 8px 8px' : '8px')};
  border-top: 0;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  height: 22px;
  user-select: none;
`;

const ItemContainer = styled.li`
  padding: 4px 8px;
  cursor: pointer;
  overflow-x: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${props => props.leftPad && `padding-left: ${props.leftPad + 8}px;`};
`;

/*
 * Multiple select component for use with react form components:
 *
 * updateValues: Callback function when a value is selected or unselected,
 * the function will be supplied an array of updated selected values.
 *
 * width: the select box width in pixels
 *
 * values: Array of objects, each object should have two or three
 * properties depending on if it is a leaf in the select tree.
 * The properties are name, which is the name displayed in the selection dropdown,
 * value, which is the value supplied to the form component in the updateValues callback,
 * then children which is an array of objects on the same form.
 *
 * initialValues: Array of the same type as the leaf node values in the values prop.
 * The initialValues prop sets the initially selected values of the component,
 * the component defaults to all values selected if this prop is omitted or no values
 * are supplied in the initialValues array.
 *
 * The object tree can be arbitrarily deep but only the leaf node values
 * are submitted in the updateValues callback function.
 * Future work on this component will be to return not only the leaf node values but all
 * selected values instead in a similar hierarchy as the values prop.
 * Below is an example of how the values array might look.
 *  [
 *    {
 *      name: 'Tester1',
 *      value: 2404932569,
 *      children: [
 *        {
 *          name: 'Testfolio1',
 *          value: 'testfolioid1',
 *          children: [
 *            {
 *              name: 'Testfolio5',
 *              value: 'testfolio5',
 *            },
 *            {
 *              name: 'Testfolio6',
 *              value: 'testfolio6',
 *            },
 *          ]
 *        },
 *        {
 *          name: 'Testfolio2',
 *          value: 'testfolioid2',
 *        },
 *      ],
 *    },
 *    {
 *      name: 'Tester2',
 *      value: 2111111569,
 *      children: [
 *        {
 *          name: 'Testfolio3',
 *          value: 'testfolioid3',
 *        },
 *        {
 *          name: 'Testfolio4',
 *          value: 'testfolioid4',
 *        },
 *      ],
 *    },
 *  ];
 */
class MultiSelectDropdown extends React.Component {
  constructor(props) {
    super(props);
    const { values, initialValues } = this.props;
    let selected = [];
    if (initialValues && initialValues.length > 0) {
      selected = initialValues;
    } else {
      selected = reduce(values, (lis, val) => lis.concat(this.getLeafValues(val)), []);
    }
    this.state = { selectedFormikVals: selected, expanded: false };
    this.props.updateValues(selected);
  }

  getChildValues(val) {
    // If val has children add all child values to the list
    // and recursively explore every childs children nodes.
    if (val.children && val.children.length > 0) {
      return reduce(
        val.children,
        (children, child) => children.concat(this.getChildValues(child).concat(child.value)),
        [],
      );
    }
    return [];
  }

  getLeafValues(val) {
    if (val.children && val.children.length > 0) {
      return reduce(
        val.children,
        (children, child) => children.concat(this.getLeafValues(child)),
        [],
      );
    }
    return [val.value];
  }

  getCheckValue(val) {
    if (val.children && val.children.length > 0) {
      return reduce(
        val.children,
        (areChecked, child) => areChecked && this.getCheckValue(child),
        true,
      );
    }
    return this.state.selectedFormikVals.includes(val.value);
  }

  handleChange(val) {
    const { updateValues } = this.props;
    const { selectedFormikVals } = this.state;
    let updatedFormikVals;
    // The array functions below do not mutate the state, they return new arrays so
    // selectedVals / selectedFormikVals are never mutated.
    if (this.getCheckValue(val)) {
      const removeFormikVals = this.getLeafValues(val);
      updatedFormikVals = selectedFormikVals.filter(el => !removeFormikVals.includes(el));
    } else {
      updatedFormikVals = selectedFormikVals.concat(
        this.getLeafValues(val).filter(el => !selectedFormikVals.includes(el)),
      );
    }
    this.setState({ selectedFormikVals: updatedFormikVals });
    updateValues(updatedFormikVals);
  }

  toggleExpand() {
    this.setState(prevState => ({ expanded: !prevState.expanded }));
  }
  renderValues(vals, leftPad = 0) {
    return vals.map(s => (
      <React.Fragment key={uniqueId()}>
        <ItemContainer
          leftPad={leftPad}
          key={s.value}
          value={s.value}
          onClick={() => this.handleChange(s)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              this.handleChange(s);
              e.stopPropagation();
              e.preventDefault();
            }
          }}
          tabIndex={-1}
        >
          <input
            type="checkbox"
            checked={this.getCheckValue(s)}
            onChange={(e) => {
              this.handleChange(s);
              e.stopPropagation();
            }}
            style={{ cursor: 'pointer' }}
          />
          {s.name}
        </ItemContainer>
        {s.children && s.children.length > 0 && this.renderValues(s.children, leftPad + 8)}
      </React.Fragment>
    ));
  }
  render() {
    const {
      title, values, width, error,
    } = this.props;
    const { expanded } = this.state;
    return (
      <div>
        <LabelContainer>
          <StyledLabel>{title}</StyledLabel>
        </LabelContainer>
        <div
          role="listbox"
          tabIndex={0}
          onBlur={(e) => {
            if (expanded && !this.multiSelectRef.contains(e.relatedTarget)) {
              this.toggleExpand();
            }
          }}
          ref={(multiSelectRef) => {
            this.multiSelectRef = multiSelectRef;
          }}
          onKeyDown={(e) => {
            // close dropdown on escape key press
            // escape key does not trigger onkeypress event
            if (e.keyCode === 27) {
              this.toggleExpand();
            }
            if (e.key === 'Enter') {
              this.toggleExpand();
              e.stopPropagation();
            }
          }}
          style={{ outline: 'none' }}
        >
          <MultiSelectContainer
            width={width}
            onClick={() => this.toggleExpand()}
            expanded={expanded}
            error={error}
          >
            <HeaderContainer>
              <div style={{ userSelect: 'none' }}>{title}</div>
              {expanded ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
            </HeaderContainer>
          </MultiSelectContainer>
          {expanded && (
            <div style={{ position: 'relative', outline: 'none' }}>
              <MultiSelectContentContainer width={width} expanded={expanded}>
                <ul style={{ listStyleType: 'none', margin: 0, padding: 0 }}>
                  {expanded && this.renderValues(values)}
                </ul>
              </MultiSelectContentContainer>
            </div>
          )}
        </div>
      </div>
    );
  }
}

MultiSelectDropdown.propTypes = {
  initialValues: PropTypes.array,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  updateValues: PropTypes.func.isRequired,
  values: PropTypes.array.isRequired,
  width: PropTypes.string,
  error: PropTypes.string,
};

MultiSelectDropdown.defaultProps = {
  title: '',
  initialValues: [],
  error: null,
  width: '',
};

export default MultiSelectDropdown;
